// src/components/PeladaDetalhes.js
import React, { useEffect, useState, useMemo } from "react";
import api from "../api";
import "./PeladaDetalhes.css";

const fmtDateTimeBR = (iso) => {
  if (!iso) return "Não informado";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", { dateStyle: "full", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const mapStatus = (status) => {
  switch ((status || "").toLowerCase()) {
    case "open":
      return { label: "Lista Aberta", cls: "pd-badge pd-badge--open" };
    case "closed":
      return { label: "Lista Fechada", cls: "pd-badge pd-badge--closed" };
    case "scheduled":
      return { label: "Agendada", cls: "pd-badge pd-badge--scheduled" };
    case "live":
      return { label: "Ao vivo", cls: "pd-badge pd-badge--live" };
    case "finished":
      return { label: "Finalizada", cls: "pd-badge pd-badge--finished" };
    case "canceled":
    case "cancelled":
      return { label: "Cancelada", cls: "pd-badge pd-badge--canceled" };
    default:
      return { label: "Indefinido", cls: "pd-badge pd-badge--unknown" };
  }
};

const isToday = (d) => {
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
};
const isTomorrow = (d) => {
  const n = new Date();
  const t = new Date(n);
  t.setDate(n.getDate() + 1);
  return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
};
const relTime = (iso) => {
  if (!iso) return null;
  const now = new Date();
  const dt = new Date(iso);
  const diff = dt.getTime() - now.getTime();
  const sign = diff >= 0 ? 1 : -1;
  const abs = Math.abs(diff);
  const m = Math.floor(abs / 60000);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  const mm = m % 60;
  if (sign >= 0) {
    if (d > 0) return `em ${d}d${h % 24 ? ` ${h % 24}h` : ""}`.trim();
    if (h > 0) return `em ${h}h${mm ? ` ${mm}m` : ""}`.trim();
    if (m > 0) return `em ${m}m`;
    return "agora";
  } else {
    if (d > 0) return `há ${d}d`;
    if (h > 0) return `há ${h}h${mm ? ` ${mm}m` : ""}`.trim();
    if (m > 0) return `há ${m}m`;
    return "agora";
  }
};

const formatWeekday = (wd) => {
  if (wd === null || wd === undefined) return null;
  const MAP = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
  if (typeof wd === "number") return MAP[wd % 7];
  const s = String(wd).toLowerCase();
  const dict = {
    "0": "Dom", "1": "Seg", "2": "Ter", "3": "Qua", "4": "Qui", "5": "Sex", "6": "Sáb",
    dom: "Dom", domingo: "Dom",
    seg: "Seg", segunda: "Seg",
    ter: "Ter", terca: "Ter", terça: "Ter",
    qua: "Qua", quarta: "Qua",
    qui: "Qui", quinta: "Qui",
    sex: "Sex", sexta: "Sex",
    sab: "Sáb", sábado: "Sáb", sabado: "Sáb",
  };
  return dict[s] || wd;
};

const PeladaDetalhes = ({ peladaId: groupId, onStartLoading, onFinishLoading }) => {
  const [s, setS] = useState(null);     // sessão
  const [g, setG] = useState(null);     // grupo (config)
  const [copied, setCopied] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const run = async () => {
      onStartLoading?.();
      setError("");
      try {
        const token = localStorage.getItem("authToken");
        const headers = { Authorization: `Bearer ${token}` };
        const [sess, grp] = await Promise.all([
          api.get(`/api/groups/${groupId}/next-session`, { headers }).catch(() => ({ data: null })),
          api.get(`/api/groups/${groupId}`, { headers }).catch(() => ({ data: null })),
        ]);
        setS(sess?.data || null);
        setG(grp?.data || null);
      } catch (err) {
        setError(err?.response?.data?.error || "Erro ao carregar detalhes.");
      } finally {
        onFinishLoading?.();
      }
    };
    if (groupId) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const placeName = s?.place_name || g?.place_name || "Jogo";
  const placeAddress = s?.place_address || g?.place_address || "";
  const mapUrl = useMemo(() => {
    const q = [placeName, placeAddress].filter(Boolean).join(" ");
    return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : null;
  }, [placeName, placeAddress]);

  const dt = s?.scheduled_at ? new Date(s.scheduled_at) : null;
  const chipsTempo = useMemo(() => {
    const arr = [];
    if (dt) {
      if (isToday(dt)) arr.push("Hoje");
      else if (isTomorrow(dt)) arr.push("Amanhã");
      const r = relTime(s.scheduled_at);
      if (r) arr.push(r);
    }
    return arr;
  }, [dt, s?.scheduled_at]);

  const statusObj = mapStatus(s?.status);

  const capLinha = g?.max_players_line ?? g?.max_players ?? null;
  const capGk = g?.max_goalkeepers ?? null;
  const capTotal = (capLinha || 0) + (capGk || 0);
  const confirmed = s?.confirmed_count ?? 0;
  const vagas = capTotal > 0 ? Math.max(capTotal - confirmed, 0) : null;
  const percent = capTotal > 0 ? Math.min(100, Math.round((confirmed / capTotal) * 100)) : 0;
  const barClass =
    percent >= 100 ? "progress-bar full" : percent >= 75 ? "progress-bar warn" : "progress-bar";

  const fee = g?.fee_amount != null ? Number(g.fee_amount) : null;
  const mensal = g?.monthly_amount != null ? Number(g.monthly_amount) : null;
  const pix = g?.pix_key || "";

  const fmtMoeda = (n) =>
    typeof n === "number" ? n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—";

  const handleCopy = async (text, label) => {
    try {
      await navigator.clipboard?.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(""), 1500);
    } catch {
      alert("Copie manualmente: " + text);
    }
  };

  if (error) return <p className="error-message">{error}</p>;

  if (!s) {
    return (
      <div className="pdet">
        <div className="pdet-empty">
          <div className="empty-emoji">📅</div>
          <div className="empty-title">Sem sessão agendada</div>
          <div className="empty-sub">Este grupo ainda não possui uma próxima pelada.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="pdet">
      {/* Cabeçalho */}
      <div className="pdet-head">
        <div className="pdet-title">
          <h3 title={placeName}>{placeName}</h3>
          {statusObj && <span className={statusObj.cls}>{statusObj.label}</span>}
        </div>
        <div className="pdet-meta">
          {/* weekday / time_of_day do grupo */}
          {g?.weekday != null && <span className="chip">{formatWeekday(g.weekday)}</span>}
          {g?.time_of_day && <span className="chip">{g.time_of_day}</span>}

          <span className="chip">Confirmados <strong>{confirmed}</strong></span>
          {capTotal > 0 && <span className="chip">Vagas <strong>{vagas}</strong></span>}

          {chipsTempo.map((c) => (
            <span key={c} className="chip">{c}</span>
          ))}
          {s.id && <span className="chip">Sessão #{String(s.id).slice(0, 8)}</span>}
        </div>
      </div>

      {/* Conteúdo */}
      <div className="pdet-grid">
        {/* Quando & Onde */}
        <section className="pdet-card">
          <header className="pdet-card-head">
            <h4>Quando & Onde</h4>
          </header>
          <div className="pdet-card-body">
            <div className="pd-row">
              <span className="pd-label">Data e hora</span>
              <span className="pd-value">{fmtDateTimeBR(s.scheduled_at)}</span>
            </div>
            <div className="pd-row">
              <span className="pd-label">Local</span>
              <span className="pd-value">{placeName}</span>
            </div>
            <div className="pd-row">
              <span className="pd-label">Endereço</span>
              <span className="pd-value">{placeAddress || "Não informado"}</span>
            </div>

            <div className="pdet-actions">
              {mapUrl && (
                <a href={mapUrl} target="_blank" rel="noreferrer" className="btn-ghost">
                  Abrir no Maps
                </a>
              )}
              {placeAddress && (
                <button className="btn-ghost" onClick={() => handleCopy(placeAddress, "endereco")}>
                  Copiar endereço
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Capacidade & Valores */}
        <section className="pdet-card">
          <header className="pdet-card-head">
            <h4>Capacidade & Valores</h4>
          </header>
          <div className="pdet-card-body">
            {/* Progresso de vagas */}
            {capTotal > 0 && (
              <div className="cap-progress" aria-label="Progresso de vagas">
                <div className="cap-progress-top">
                  <span className="muted">Ocupação</span>
                  <span className="muted">
                    <strong>{confirmed}</strong> / {capTotal}
                  </span>
                </div>
                <div
                  className="progress"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={capTotal}
                  aria-valuenow={confirmed}
                >
                  <div className={barClass} style={{ width: `${percent}%` }} />
                </div>
              </div>
            )}

            {/* Capacidade */}
            <div className="pd-row">
              <span className="pd-label">Capacidade (Linha)</span>
              <span className="pd-value">{capLinha != null ? capLinha : "—"}</span>
            </div>
            <div className="pd-row">
              <span className="pd-label">Capacidade (Goleiros)</span>
              <span className="pd-value">{capGk != null ? capGk : "—"}</span>
            </div>
            <div className="pd-row">
              <span className="pd-label">Total</span>
              <span className="pd-value">{capTotal > 0 ? capTotal : "—"}</span>
            </div>
            <div className="pd-row">
              <span className="pd-label">Vagas restantes</span>
              <span className="pd-value">{capTotal > 0 ? Math.max(capTotal - confirmed, 0) : "—"}</span>
            </div>

            <hr className="pdet-sep" />

            {/* Valores */}
            <div className="pd-row">
              <span className="pd-label">Taxa avulsa</span>
              <span className="pd-value">{fmtMoeda(fee)}</span>
            </div>
            <div className="pd-row">
              <span className="pd-label">Mensalidade</span>
              <span className="pd-value">{fmtMoeda(mensal)}</span>
            </div>
            <div className="pd-row">
              <span className="pd-label">PIX</span>
              <span className="pd-value">{pix || "—"}</span>
            </div>

            {pix && (
              <div className="pdet-actions">
                <button className="btn-ghost" onClick={() => handleCopy(pix, "pix")}>
                  Copiar chave PIX
                </button>
                {copied === "pix" && <span className="mini-toast">Pix copiado!</span>}
              </div>
            )}
          </div>
        </section>

        {/* Técnicos */}
        <section className="pdet-card">
          <header className="pdet-card-head">
            <h4>Dados técnicos</h4>
          </header>
          <div className="pdet-card-body">
            <div className="pd-row">
              <span className="pd-label">Status da lista</span>
              <span className="pd-value">{mapStatus(s?.status).label}</span>
            </div>
            <div className="pd-row">
              <span className="pd-label">ID da sessão</span>
              <span className="pd-value">{s.id || "—"}</span>
            </div>
            <div className="pd-row">
              <span className="pd-label">Grupo</span>
              <span className="pd-value">{s.group_id || groupId || "—"}</span>
            </div>
          </div>
        </section>
      </div>

      {/* mini toast de cópia do endereço */}
      {copied === "endereco" && <div className="mini-toast">Endereço copiado!</div>}
    </div>
  );
};

export default PeladaDetalhes;
